import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useMediaQuery } from "react-responsive";
import styled from "styled-components";
import { Button, TextLink } from "@vds/buttons";
import { Icon } from "@vds/icons";
import { Modal, ModalTitle, ModalBody } from "@vds/modals";
import { TileContainer } from "@vds/tiles";
import { TrailingTooltip } from "@vds/tooltips";
import { Text } from '@vds/core';
import { useImageUtils } from "hooks";
import BroadbandPlan from "common/components/FWA/PlanSelection/BroadbandPlan";
import { addToCart } from "pages/Mobility/ducks/MobilitySlice";
import {
  // storeFccDetails,
  updateFCCJson,
  updateFCCTemplate,
} from "pages/Plans/ducks/PlansSlice";
import { DeviceContainer } from "./styles";

const BuyNowCard = ({ aem }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({
    query: "(max-width: 445px)",
  });
  const { getImageLink } = useImageUtils();
  const zipCodeForBuyNowFlow = useSelector(
    (state) => state.MobilityPage?.zipCodeForBuyNowFlow
  );
const pageData = useSelector((state) => state.WirelessPlans?.htmlTemplateFcc ? state.WirelessPlans : state.PlanFirst);
  const [showModal, setShowModal] = useState(false);
  const [isAddToCartClicked, setIsAddToCartClicked] = useState(false);

  useEffect(() => {
    if(showModal && !pageData?.htmlTemplateFcc &&!pageData?.planDetailsFcc?.length){
      dispatch(updateFCCTemplate());
      dispatch(updateFCCJson(["32066"]));
    }
  }, [showModal]);

  const addToCartClick = () => {
    setIsAddToCartClicked(true);
    const payload = {
      cartMeta: {
        zipCode: zipCodeForBuyNowFlow ?? "30004",
        flow: "prospect",
        shoppingPath: "prospect",
        isBulkAal: false,
        pdpBillingAddress: {},
        buyNowNse: aem?.useBuyNowNseKey ?? false,
      },
      approach: "plans-first",
      lines: [aem?.addToCartPayload],
    };
    dispatch(
      addToCart({
        ...payload,
        routeTo: () => {
          navigate("/shop/shopping-cart");
        },
      })
    );
  };

  return (
    <>
      <DeviceContainer>      
        <TileContainer
          backgroundColor={aem?.backgroundColor ?? "white"}
          padding={isMobile ? "24px 12px" : "24px"}
          onClick={(e) => setShowModal(true)}
          width="100%"
          height="100%"
          >
             {!!aem?.cap && (
            <Cap
              icon="tag"
              bold={aem.cap?.bold}
              backgroundColor={aem.cap?.backgroundColor || "black"}
              characterLimit={50}
            >
              {aem.cap?.text}
            </Cap>
          )}          
          <CardContent>
            <TitleSection>
              <Text kind="title" size="small" bold>
                {aem?.title}
              </Text>
              {!!aem?.tooltip ? (
                <TrailingTooltip
                  typographyType="body"
                  typographySize="small"
                  typographyPrimitive="span"
                  tooltipContent={aem.offerDetails}
                >
                  {aem?.subtitle}
                </TrailingTooltip>
              ) : (
                <>
                  <Text kind="body" size="small">
                    {aem?.subtitle}&nbsp;
                    {!!aem?.detailsLink && (
                      <TextLink
                        type="inline"
                        size="small"
                        onClick={(e) => setShowModal(true)}
                      >
                        {aem?.detailsLink}
                      </TextLink>
                    )}
                  </Text>
                </>
              )}
            </TitleSection>
            <DetailSection>
              <picture>
                <source
                  type="image/webp"
                  srcset={getImageLink(aem?.includes?.ss7Img, 96, 96, true)}
                />
                <img
                  src={getImageLink(aem?.includes?.ss7Img, 96, 96, true)}
                  alt={aem?.includes?.imgAlt}
                />
              </picture>
              <IncludesWrapper>
                <Text kind="body" size="small" bold>
                  {aem?.includes?.title}
                </Text>
                <IncludesList>
                  {aem?.includes?.items?.map((item, index) => (
                    <IncludesItem key={index}>
                      <Icon name={item?.icon} size="small" />
                      <IncludesLinkItem>
                        <Text kind="body" size="small">{item?.text}</Text>
                        {!!item?.showDetailsLink && (
                          <TextLink
                            type="standAlone"
                            size="small"
                            onClick={(e) => {
                              setShowModal(true);
                            }
                          }
                          >
                            {item?.detailsLinkText}
                          </TextLink>
                        )}
                      </IncludesLinkItem>
                    </IncludesItem>
                  ))}
                </IncludesList>
              </IncludesWrapper>
            </DetailSection>
            {!!aem?.price?.show && (
              <PricingSection>
                <Text kind="body" size="small">{aem?.price?.text}</Text>
                <div style={{ display: "flex", gap: "0.25rem" }}>
                  <Text kind="body" size="small" strikethrough>
                    {aem?.price?.packagePrice}
                  </Text>
                  <Text kind="body" size="small" bold>
                    {aem?.price?.discountedPrice}
                  </Text>
                  {!!aem?.price?.detailsLinkText && (
                    <TextLink
                      type="standAlone"
                      size="small"
                      onClick={(e) => setShowModal(true)}
                    >
                      {aem?.price?.detailsLinkText}
                    </TextLink>
                  )}
                </div>
                <Text kind="body" size="small" color="">
                  {aem?.price?.terms}
                </Text>
              </PricingSection>
            )}
            <CTASection>
              <Button
                data-track={`Buy Now:${aem?.button?.text}`}
                use="secondary"
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // setShowModal(true);
                  addToCartClick();
                }}
              >
                {aem?.button?.text}
              </Button>
            </CTASection>
          </CardContent>
        </TileContainer>
      </DeviceContainer>
      <BuyNowDetailsModal
        opened={showModal}
        onOpenedChange={setShowModal}
        title={aem?.detailsModal?.title}
        bodyText={aem?.offerDetails}
        planId={aem?.planId}
      />
    </>
  );
};

const Cap = ({ icon, bold, backgroundColor, children }) => {
  return (
    <TileCap backgroundColor={backgroundColor}>
      <Icon name={icon} color="white" size="medium" />
      <Text kind="body" size="small" bold={bold} color="white">
        {children}
      </Text>
    </TileCap>
  );
};

const BuyNowDetailsModal = ({
  opened,
  onOpenedChange,
  title,
  bodyText,
  planId,
}) => {
  return (
    <Modal opened={opened} onOpenedChange={onOpenedChange}>
      <ModalTitle>{title}</ModalTitle>
      <ModalBody>
        <ModalBodyContent>
          <Text kind="body" size="large">{bodyText}</Text>
          <div style={{ minHeight: "162px" }}>
            <BroadbandPlan planId={planId} flow="nse" />
          </div>
          <Button onClick={() => onOpenedChange(false)}>{"Close"}</Button>
        </ModalBodyContent>
      </ModalBody>
    </Modal>
  );
};

const TileCap = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1;
  width: 100%;
  background-color: ${(props) =>
    props.backgroundColor === "red" ? "#E10014" : "black"};
  height: 3rem;
  border-radius: 12px 12px 0 0;
  padding: 10px 24px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  > div {
    width: 100% !important;
    border-radius: 12px 12px 0 0;
  }
  @media (max-width: 545px) {
    padding: 10px 12px;
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-top: 3rem;
  @media (max-width: 545px) {
    margin-top: 2rem;
  }
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const DetailSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  box-sizing: border-box;
  background-color: #f6f6f6;
  border-radius: 12px;
  @media (max-width: 545px) {
    padding: 1rem 0.75rem;
  }
`;

const IncludesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const IncludesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const IncludesItem = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.25rem;
`;

const IncludesLinkItem = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.25rem;
  > a {
    width: fit-content;
    align-self: auto;
  }
  @media (max-width: 545px) {
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
  }
`;

const PricingSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const CTASection = styled.div`
  margin-top: auto;
`;


const ModalBodyContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  > button {
    width: fit-content;
  }
  .fcc-bl-main-container {
    margin: 0 !important;
    min-width: 100% !important;
  }
`;

export default React.memo(BuyNowCard);
